import { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useWebSocket } from '../../hooks/useWebSocket'
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  ArrowDropDown as ArrowDropDownIcon,
  BusinessCenter as BusinessCenterIcon,
  Domain as DomainIcon,
  FilterAltOutlined as FilterAltOutlinedIcon,
} from '@mui/icons-material'
import EmptyState from '../../components/common/EmptyState'
import JobApplicationCard from '../../components/job-applications/JobApplicationCard'
import LoadingSpinner from '../../components/LoadingSpinner'
import PageHelpButton from '../../components/PageHelpButton'
import { userAtom } from '../../state/auth'
import {
  jobOpportunitiesAtom,
  jobOpportunitiesLoadingAtom,
  JobOpportunity,
  APPLY_DECISIONS,
  APPLY_DECISION_LABELS,
  APPLY_DECISION_COLORS,
  COMPANY_TIERS,
  COMPANY_TIER_LABELS,
  COMPANY_TIER_CHIP_COLOR,
  resolveCompanyTier,
  fitScoreColor,
  type ApplyDecision,
  type ClientPursuit,
} from '../../state/jobOpportunities'
import {
  jobOpportunitiesApi,
  clientsApi,
  interviewsApi,
  Client,
  Interview,
} from '../../services/api'
import { useClientNetwork } from '../../hooks/useClientNetwork'
import { isExecutiveClient, hasWildcardOutreach } from '../../utils/clientTier'
import { formatJobDate } from '../../utils/dateRange'
import { emptyTabDescription } from '../../utils/emptyTabDescription'
import { ensureHttpProtocol } from '../../utils/validation'
import JobDetailDialog from '../../components/job-applications/JobDetailDialog'
import InterviewDetailDialog from '../../components/interview-prep/InterviewDetailDialog'
import {
  PORTAL_INTERVIEW_PARAM,
  PORTAL_INTERVIEW_SECTION_PARAM,
  readPortalInterviewRequest,
} from '../../utils/portalInterviewLink'
import { withDecisionDate } from '../../utils/appliedDate'
import HiddenOpportunitiesTab from '../../components/job-applications/HiddenOpportunitiesTab'
import PortalAddJobDialog from '../../components/job-applications/PortalAddJobDialog'
import InactivityChip from '../../components/job-applications/InactivityChip'
import ExecutiveSearchTab from '../../components/job-applications/ExecutiveSearchTab'
import TargetCompaniesTab from '../../components/job-applications/TargetCompaniesTab'
import {
  JOB_TABS as TABS,
  jobTabKey,
  jobTabLabel,
  TERMINAL_JOB_TABS,
  JobTabKey as TabKey,
} from '../../components/job-applications/jobTabRouting'
import { useTablePagination } from '../../components/common/table/useTablePagination'
import TablePaginationBar from '../../components/common/table/TablePaginationBar'
import SortableTableHeader, { SortOrder } from '../../components/common/table/SortableTableHeader'
import FilterBar, { FilterBarFilter } from '../../components/common/table/FilterBar'
import { getEnhancedTableStyles } from '../../utils/tableStyles'
import { getCompactTableSx } from '../../utils/compactTableStyles'
import { ExpanderField, ExpanderHeaderCell, ExpanderRow, ExpanderToggleCell } from '../../components/common/table/RowExpander'
import { useNotification } from '../../hooks/useNotification'

// MWA-251 — the client Job Search flow mirrors the staff page: a fixed, locked
// set of tabs (intentionally not client-configurable). Each job lands in exactly
// ONE tab via jobTabKey(), shared with the staff page — see jobTabRouting.ts.

export default function PortalJobApplications() {
  const [user] = useAtom(userAtom)
  const [jobs, setJobs] = useAtom(jobOpportunitiesAtom)
  const [loading, setLoading] = useAtom(jobOpportunitiesLoadingAtom)
  const [error, setError] = useState<string | null>(null)
  // Entity tabs (Executive Search recruiters / Target Companies) live outside the
  // jobs TabKey union and render their own components.
  const [activeTab, setActiveTab] = useState<TabKey | 'execsearch' | 'targetcompanies' | 'wildcard'>('pending_decision')
  const [addOpen, setAddOpen] = useState(false)
  // MWA-267 — anchor for the "+Add" dropdown that mirrors the staff menu.
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null)
  // The header Add menu opens the Target Companies add dialog (no in-tab Add
  // button), mirroring staff. One-shot trigger acknowledged by the tab.
  const [tcAutoAdd, setTcAutoAdd] = useState(false)
  const [selectedJob, setSelectedJob] = useState<JobOpportunity | null>(null)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  // MWA-247 — id of the job whose decision is currently being saved (spinner).
  const [decidingId, setDecidingId] = useState<string | null>(null)
  // MWA-355 — id of the hidden opportunity whose pursue/decline answer is saving.
  const [pursuitSavingId, setPursuitSavingId] = useState<string | null>(null)
  const { showSuccess, showError } = useNotification()

  // MWA-394 — the client's interview opens as a dialog over this page, from the
  // job's "View Interview" button or from a ?interview=<id> link (the "guide is
  // ready" toast). There is no Interviews page; fetched by id on demand because
  // the portal never loads the interview list.
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null)
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false)
  const [interviewSection, setInterviewSection] = useState<string | undefined>(undefined)
  const interviewRequest = readPortalInterviewRequest(searchParams)
  const requestedInterviewId = interviewRequest?.interviewId
  const requestedInterviewSection = interviewRequest?.section
  // Read by the WebSocket handler below, which is built once.
  const openInterviewIdRef = useRef<string | null>(null)
  openInterviewIdRef.current = interviewDialogOpen ? selectedInterview?.id ?? null : null

  const theme = useTheme()
  const tableStyles = getEnhancedTableStyles(theme)

  // Clients read this on a phone far more often than staff read the tracker on
  // one, so the card layout below 768px matters more here than it does there.
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  // Below 1200 the table keeps its priority columns at fixed widths and moves
  // Location and Company Tier into a row expander, as the staff board does.
  const isCompact = useMediaQuery(theme.breakpoints.down(1200))
  const compactWidth = (width: number | string | Record<string, number | string>) =>
    isCompact ? { width } : undefined
  // A chip label never wraps by default, so in a narrow column "Apply on my
  // behalf" would truncate. On the client's own decision buttons that hides
  // the choice, so at tablet widths their labels wrap onto a second line.
  const wrappingChipSx = {
    '& .MuiChip-root': { height: 'auto', minHeight: 24 },
    '& .MuiChip-label': { whiteSpace: 'normal', paddingTop: '3px', paddingBottom: '3px' },
  }

  // CrowCase-style sort + filter for the portal pipeline table (parity with staff).
  const [orderBy, setOrderBy] = useState<string>('')
  const [order, setOrder] = useState<SortOrder>('asc')
  const [jobFilterValues, setJobFilterValues] = useState<{ companyTier: string[]; decision: string[] }>({
    companyTier: [],
    decision: [],
  })

  const clientId = user?.clientId || ''

  // WebSocket handlers — keep the live list in sync (create/update/delete).
  const wsHandlers = useMemo(
    () => ({
      job_opportunity_created: (message: { data: unknown }) => {
        const newJob = message.data as JobOpportunity
        setJobs((prev) => {
          if (prev.some((j) => j.id === newJob.id)) return prev
          return [...prev, newJob]
        })
      },
      job_opportunity_updated: (message: { data: unknown }) => {
        const updated = message.data as JobOpportunity
        setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)))
      },
      job_opportunity_deleted: (message: { data: unknown }) => {
        const data = message.data as { id: string }
        setJobs((prev) => prev.filter((j) => j.id !== data.id))
      },
      // MWA-394 — staff published (or re-published) the guide while the client
      // has this interview open, so the dialog would otherwise keep showing the
      // "being reviewed" message until it is reopened.
      interview_guide_published: (message: { data: unknown }) => {
        const data = (message.data || {}) as { interviewId?: string }
        if (!data.interviewId || data.interviewId !== openInterviewIdRef.current) return
        interviewsApi
          .get(data.interviewId)
          .then((fresh) => {
            if (fresh.id === openInterviewIdRef.current) setSelectedInterview(fresh)
          })
          .catch((err) => console.warn('Failed to refresh the open interview after its guide was published:', err))
      },
    }),
    [setJobs]
  )

  useWebSocket(wsHandlers)

  const openInterview = async (interviewId: string, section?: string): Promise<boolean> => {
    try {
      const fresh = await interviewsApi.get(interviewId)
      setSelectedInterview(fresh)
      setInterviewSection(section)
      setInterviewDialogOpen(true)
      return true
    } catch (err) {
      console.error('Failed to load interview:', err)
      showError('We could not open that interview.')
      return false
    }
  }

  // The job dialog stays open, with its button busy, until the interview has
  // loaded: closing it first left the client looking at the bare page for the
  // length of the read, and a second click started a second read. The ref is
  // the guard, because state would not update before a rapid second click.
  const [viewingInterview, setViewingInterview] = useState(false)
  const viewingInterviewRef = useRef(false)
  const handleViewInterview = (interviewId: string) => {
    if (viewingInterviewRef.current) return
    viewingInterviewRef.current = true
    setViewingInterview(true)
    openInterview(interviewId)
      .then((opened) => {
        if (opened) setDetailDialogOpen(false)
      })
      .finally(() => {
        viewingInterviewRef.current = false
        setViewingInterview(false)
      })
  }

  const dropInterviewParams = () => {
    if (!searchParams.has(PORTAL_INTERVIEW_PARAM) && !searchParams.has(PORTAL_INTERVIEW_SECTION_PARAM)) return
    const next = new URLSearchParams(searchParams)
    next.delete(PORTAL_INTERVIEW_PARAM)
    next.delete(PORTAL_INTERVIEW_SECTION_PARAM)
    setSearchParams(next, { replace: true })
  }

  // A link naming an interview opens it. On a miss (not this client's, or gone)
  // the param is dropped so a reload does not retry a dead link.
  useEffect(() => {
    if (!requestedInterviewId) return
    openInterview(requestedInterviewId, requestedInterviewSection).then((opened) => {
      if (!opened) dropInterviewParams()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedInterviewId, requestedInterviewSection])

  const handleInterviewClose = () => {
    setInterviewDialogOpen(false)
    setInterviewSection(undefined)
    dropInterviewParams()
  }

  useEffect(() => {
    if (clientId) loadJobs()
  }, [clientId])

  // MWA-308 — Search Firm outreach is an executive-only service, so the tab is
  // gated on the client's own tier. get-client already serves a client their
  // own record, so this needs no new endpoint.
  //
  // Unknown reads as hidden, which is the safe direction: the tier is absent
  // until someone classifies the client, and showing a service they are not on
  // is worse than a tab appearing a moment after load. `tierLoadFailed` is
  // tracked separately so a transient error is not silently indistinguishable
  // from a deliberate standard tier.
  const [client, setClient] = useState<Client | null>(null)
  const [tierLoadFailed, setTierLoadFailed] = useState(false)
  useEffect(() => {
    if (!clientId) {
      setClient(null)
      return
    }
    let cancelled = false
    setTierLoadFailed(false)
    clientsApi
      .get(clientId)
      .then((res) => {
        if (!cancelled) setClient(res)
      })
      .catch((err) => {
        console.error('Failed to read the client record for tier gating:', err)
        if (!cancelled) {
          setClient(null)
          setTierLoadFailed(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [clientId])

  const canSeeExecSearch = isExecutiveClient(client)
  // MWA-357 — switched on per client by staff; hidden here otherwise.
  const canSeeWildcard = hasWildcardOutreach(client)

  // Leaving activeTab on a tab that is no longer rendered makes MUI warn and
  // shows the client an empty panel, so fall back to the first tab.
  useEffect(() => {
    if (activeTab === 'execsearch' && !canSeeExecSearch) {
      setActiveTab('pending_decision')
    }
    if (activeTab === 'wildcard' && !canSeeWildcard) {
      setActiveTab('pending_decision')
    }
  }, [activeTab, canSeeExecSearch, canSeeWildcard])

  // Which row the tablet layout has opened. Reset with the tab, so an expander
  // never reopens against a different application in the same position.
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)
  useEffect(() => {
    setExpandedRowId(null)
  }, [activeTab])

  // MWA-297 — the client's own contacts, so the job dialog can show who is
  // attached to an application. Read-only here: the dialog hides the attach
  // and remove controls for a client, and update-job-opportunity rejects the
  // field from a client role regardless. Interactions come in the same hook, in
  // one request, so the dialog can show what has been said to each contact.
  const network = useClientNetwork(clientId ?? null)
  const { contacts } = network

  const loadJobs = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await jobOpportunitiesApi.list({ clientId })
      setJobs(Array.isArray(result) ? result : [])
    } catch (err) {
      console.error('Failed to load job applications:', err)
      setError('Failed to load your job applications.')
    } finally {
      setLoading(false)
    }
  }

  const handleJobClick = (job: JobOpportunity) => {
    setSelectedJob(job)
    setDetailDialogOpen(true)
  }

  const handleJobUpdate = (updated: JobOpportunity) => {
    setJobs(jobs.map((j) => (j.id === updated.id ? updated : j)))
    setSelectedJob(updated)
  }

  const handleJobDelete = (id: string) => {
    setJobs(jobs.filter((j) => j.id !== id))
  }

  // MWA-247 — client picks a decision inline on an awaiting-decision row. Writes
  // applyDecision via the same update endpoint the detail dialog uses, then
  // syncs local state (stripping the transient hasDuplicate flag).
  const handleDecision = async (jobId: string, decision: ApplyDecision) => {
    setDecidingId(jobId)
    try {
      const result = await jobOpportunitiesApi.update(withDecisionDate({ id: jobId, applyDecision: decision }))
      setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...result, hasDuplicate: undefined } : j)))
      showSuccess(`Decision saved: ${APPLY_DECISION_LABELS[decision] || decision}`)
    } catch (err) {
      console.error('Failed to save decision:', err)
      showError('Failed to save your decision')
    } finally {
      setDecidingId(null)
    }
  }

  // MWA-355 — the client answers whether they will chase a hidden opportunity.
  // The only field a client may write on one; the API refuses the rest.
  //
  // Deliberately not optimistic. A decline removes the card from this list, so
  // an optimistic write would make it vanish and then reappear on failure,
  // which reads as the app losing the answer rather than as an error. The list
  // changes only once the server has the answer.
  const handlePursuit = async (job: JobOpportunity, pursuit: ClientPursuit) => {
    setPursuitSavingId(job.id)
    try {
      const result = await jobOpportunitiesApi.update({ id: job.id, clientPursuit: pursuit })
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...result, hasDuplicate: undefined } : j)))
      showSuccess(
        pursuit === 'declined'
          ? `Thanks — we won't chase ${job.company}.`
          : `Noted — we'll keep working on ${job.company}.`
      )
    } catch (err) {
      console.error('Failed to save the pursuit answer:', err)
      showError('We could not save your answer. Please try again.')
    } finally {
      setPursuitSavingId(null)
    }
  }

  const handleCreateJob = async (data: { company: string; jobTitle: string; applicationDate?: string; url?: string; notes?: string; location?: string; status?: string; awaitingDecision?: boolean }) => {
    try {
      const newJob = await jobOpportunitiesApi.create({
        clientId,
        clientName: user?.name,
        ...data,
      })
      setJobs([...jobs, newJob])
      // MWA-367 — the status picked decides the tab, so open that tab rather
      // than leaving the client on one the new row is not in.
      setActiveTab(jobTabKey(newJob))
      showSuccess(data.awaitingDecision ? 'Opportunity added. Choose how you would like to proceed.' : 'Opportunity added.')
      setAddOpen(false)
    } catch {
      showError('Failed to add the opportunity')
    }
  }

  // MWA-267 — "+Add" dropdown handlers (mirrors the staff page).
  const openAddMenu = (e: React.MouseEvent<HTMLElement>) => setAddMenuAnchor(e.currentTarget)
  const closeAddMenu = () => setAddMenuAnchor(null)

  // MWA-355 — what the client is shown, as opposed to what the board holds.
  // An opportunity they said they would not pursue leaves their view; staff
  // keep seeing it, with the decline and its date, on the staff page.
  //
  // Filtered here, at the single source both the tabs and their badge counts
  // read, rather than in the Hidden Opportunities tab itself — filtering only
  // the rendered list would leave the badge counting rows the client cannot
  // see. Deliberately not done in jobTabKey either: that router is shared with
  // the staff page, and hiding it there would hide it from staff too.
  const visibleJobs = useMemo(
    () => jobs.filter((j) => !(j.hiddenOpportunity && j.clientPursuit === 'declined')),
    [jobs]
  )

  // Group jobs into the fixed tabs (each job in exactly one tab).
  const jobsByTab = useMemo(() => {
    const grouped: Record<TabKey, JobOpportunity[]> = {
      hidden_opportunity: [],
      pending_decision: [],
      applied: [],
      following_up: [],
      interviewing: [],
      got_the_job: [],
      rejected: [],
      closed_not_accepting: [],
      archived: [],
    }
    for (const j of visibleJobs) grouped[jobTabKey(j)].push(j)
    // Most-recent-activity first within each tab.
    for (const key of Object.keys(grouped) as TabKey[]) {
      grouped[key].sort(
        (a, b) => new Date(b.lastActivityDate || b.updatedAt).getTime() - new Date(a.lastActivityDate || a.updatedAt).getTime()
      )
    }
    return grouped
  }, [visibleJobs])

  const rows: JobOpportunity[] = activeTab !== 'execsearch' && activeTab !== 'targetcompanies' && activeTab !== 'wildcard' ? jobsByTab[activeTab] : []

  const handleSort = (columnId: string) => {
    if (orderBy === columnId) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(columnId)
      setOrder('asc')
    }
  }

  const jobFilters: FilterBarFilter[] = [
    {
      name: 'companyTier',
      label: 'Company Tier',
      options: COMPANY_TIERS.map((c) => ({ value: c.value, label: c.label })),
      value: jobFilterValues.companyTier,
    },
    {
      name: 'decision',
      label: 'Your Decision',
      options: [
        ...APPLY_DECISIONS.map((d) => ({ value: d.value, label: d.label })),
        { value: 'pending', label: 'Awaiting your decision' },
      ],
      value: jobFilterValues.decision,
    },
  ]
  const handleJobFilterChange = (name: string, value: string[]) =>
    setJobFilterValues((prev) => ({ ...prev, [name]: value }))
  const clearJobFilters = () => setJobFilterValues({ companyTier: [], decision: [] })
  const hasActiveJobFilters = jobFilterValues.companyTier.length > 0 || jobFilterValues.decision.length > 0

  // Mirror the on-screen Your-Decision rendering so the column sorts by what's shown.
  const decisionSortLabel = (j: JobOpportunity) =>
    j.applyDecision
      ? APPLY_DECISION_LABELS[j.applyDecision] || j.applyDecision
      : j.decisionRequested && !j.archived
        ? 'Awaiting your decision'
        : ''

  // Shared by displayRows and jobCountsByTab, so the tab badges and the
  // visible rows always agree on what "filtered" means.
  const jobMatchesFilters = (j: JobOpportunity) => {
    if (jobFilterValues.companyTier.length > 0) {
      if (!jobFilterValues.companyTier.includes(resolveCompanyTier(j.companyTier))) return false
    }
    if (jobFilterValues.decision.length > 0) {
      const match = jobFilterValues.decision.some((d) =>
        d === 'pending'
          ? !!j.decisionRequested && !j.applyDecision && !j.archived
          : j.applyDecision === d
      )
      if (!match) return false
    }
    return true
  }

  // Tab badge counts reflect the active companyTier/decision filters, matching
  // NetworkingContactsTable's category tabs, instead of always showing the
  // unfiltered per-tab total.
  const jobCountsByTab = useMemo(() => {
    const grouped: Record<TabKey, number> = {
      hidden_opportunity: 0,
      pending_decision: 0,
      applied: 0,
      following_up: 0,
      interviewing: 0,
      got_the_job: 0,
      rejected: 0,
      closed_not_accepting: 0,
      archived: 0,
    }
    for (const j of visibleJobs) {
      if (jobMatchesFilters(j)) grouped[jobTabKey(j)]++
    }
    return grouped
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleJobs, jobFilterValues])

  const displayRows = useMemo(() => {
    const filtered = rows.filter(jobMatchesFilters)

    if (!orderBy) return filtered

    return filtered.slice().sort((a, b) => {
      let aVal: string | number
      let bVal: string | number
      switch (orderBy) {
        case 'company':
          aVal = a.company || ''
          bVal = b.company || ''
          break
        case 'jobTitle':
          aVal = a.jobTitle || ''
          bVal = b.jobTitle || ''
          break
        case 'fit':
          aVal = a.fitScore ?? -1
          bVal = b.fitScore ?? -1
          break
        case 'decision':
          aVal = decisionSortLabel(a)
          bVal = decisionSortLabel(b)
          break
        case 'location':
          aVal = a.location || ''
          bVal = b.location || ''
          break
        case 'applied':
          aVal = a.applicationDate ? new Date(a.applicationDate).getTime() : 0
          bVal = b.applicationDate ? new Date(b.applicationDate).getTime() : 0
          break
        default:
          return 0
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return order === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, orderBy, order, jobFilterValues])

  // MWA-254 Defect 2 — client-side pagination over the filtered/sorted rows.
  // resetKey jumps back to page 1 when the active tab, sort, or filters change.
  const { paginated: pagedRows, ...pageCtl } = useTablePagination(
    displayRows,
    `${activeTab}|${orderBy}|${order}|${jobFilterValues.companyTier.join(',')}|${jobFilterValues.decision.join(',')}`
  )

  const formatApplied = (dateStr?: string) =>
    formatJobDate(dateStr, 'en-US', { month: 'short', day: 'numeric' })

  const renderCompanyTier = (j: JobOpportunity) => (
    <Chip
      label={COMPANY_TIER_LABELS[resolveCompanyTier(j.companyTier)]}
      size="small"
      color={COMPANY_TIER_CHIP_COLOR[resolveCompanyTier(j.companyTier)]}
      sx={{ height: 18, typography: 'badge' }}
    />
  )

  // MWA-247 — the "Your Decision" cell. An awaiting-decision row (decision
  // requested, none picked, not archived) shows selectable pills; a decided row
  // shows the read-only chip; everything else shows a dash.
  const renderDecision = (j: JobOpportunity) => {
    const awaiting = j.decisionRequested && !j.applyDecision && !j.archived
    if (awaiting) {
      const busy = decidingId === j.id
      return (
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          {APPLY_DECISIONS.map((d) => (
            <Chip
              key={d.value}
              label={
                busy ? (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <CircularProgress size={12} sx={{ color: APPLY_DECISION_COLORS[d.value] }} />
                    {d.label}
                  </Box>
                ) : (
                  d.label
                )
              }
              size="small"
              clickable
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation()
                if (!busy) handleDecision(j.id, d.value)
              }}
              variant="outlined"
              sx={{
                borderColor: APPLY_DECISION_COLORS[d.value],
                color: APPLY_DECISION_COLORS[d.value],
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: APPLY_DECISION_COLORS[d.value],
                  color: 'white',
                },
              }}
            />
          ))}
        </Box>
      )
    }
    if (j.applyDecision) {
      return (
        <Chip
          label={APPLY_DECISION_LABELS[j.applyDecision] || j.applyDecision}
          size="small"
          sx={{ backgroundColor: APPLY_DECISION_COLORS[j.applyDecision], color: 'white', fontWeight: 500 }}
        />
      )
    }
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  if (loading && jobs.length === 0) return <LoadingSpinner />

  const activeTabDefinition = TABS.find((t) => t.key === activeTab)
  const activeTabLabel = activeTabDefinition ? jobTabLabel(activeTabDefinition, 'client') : ''

  return (
    <Box sx={{ height: { xs: 'calc(100vh - 100px)', sm: 'calc(100vh - 120px)' }, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" component="h1">Front Door</Typography>
          <PageHelpButton articleId="page-portal-job-applications" />
        </Stack>
        {/* MWA-267 — portal "+Add" mirrors the staff dropdown (same pattern),
            minus Add Recruiter: Executive Search stays staff-curated. */}
        <Button variant="contained" startIcon={<AddIcon />} endIcon={<ArrowDropDownIcon />} onClick={openAddMenu}>
          Add
        </Button>
        <Menu anchorEl={addMenuAnchor} open={!!addMenuAnchor} onClose={closeAddMenu}>
          <MenuItem
            onClick={() => {
              closeAddMenu()
              setAddOpen(true)
            }}
          >
            <ListItemIcon><BusinessCenterIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Add Opportunity</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeAddMenu()
              setActiveTab('targetcompanies')
              if (clientId) setTcAutoAdd(true)
            }}
          >
            <ListItemIcon><DomainIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Add Target Company</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* MWA-308 — without this, a failed read of the client record is
          indistinguishable from a standard tier: the tab is simply absent and
          nobody can tell whether that was intended. */}
      {tierLoadFailed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          We couldn&rsquo;t load your account details, so some sections may be missing.
          Refresh the page to try again.
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v as TabKey | 'execsearch' | 'targetcompanies' | 'wildcard')}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {jobTabLabel(t, 'client')}
                <Chip label={jobCountsByTab[t.key]} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
              </Box>
            }
          />
        ))}
        {/* MWA-254 — Target Companies (read-only for the client; staff curate them). */}
        <Tab value="targetcompanies" label="Target Companies" />
        {/* MWA-253 — Executive Search recruiters (read + copy; staff curate them).
            MWA-308 — executive-tier clients only. */}
        {canSeeExecSearch && <Tab value="execsearch" label="Executive Search" />}
        {/* MWA-357 — Wild Card Outreach, a functional copy of Executive Search. */}
        {canSeeWildcard && <Tab value="wildcard" label="Wild Card Outreach" />}
      </Tabs>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 'execsearch' && canSeeExecSearch ? (
          // MWA-260 — portal clients can mark recruiters contacted (not add/edit/delete);
          // MWA-356 — and mark followed up and log what the partner said back.
          <ExecutiveSearchTab key="execsearch" clientId={clientId} canEdit={false} canMarkContacted canLogOutcome />
        ) : activeTab === 'wildcard' && canSeeWildcard ? (
          <ExecutiveSearchTab
            key="wildcard"
            outreachType="wildcard"
            clientId={clientId}
            canEdit={false}
            canMarkContacted
            canLogOutcome
          />
        ) : activeTab === 'hidden_opportunity' ? (
          <HiddenOpportunitiesTab
            jobs={jobsByTab.hidden_opportunity}
            contacts={contacts}
            onOpen={handleJobClick}
            audience="client"
            onPursuit={handlePursuit}
            savingPursuitId={pursuitSavingId}
            emptyHint={'Nothing here yet. When we spot a role opening before it is advertised, it will appear here.'}
          />
        ) : activeTab === 'targetcompanies' ? (
          // MWA-254 Defect 4 — full parity: portal clients can add/edit/delete their own target companies.
          // Add is driven by the header Add menu (no in-tab Add button), mirroring staff.
          <TargetCompaniesTab
            clientId={clientId}
            canEdit
            showAddButton={false}
            autoOpenAdd={tcAutoAdd}
            onAutoAddHandled={() => setTcAutoAdd(false)}
          />
        ) : (
          <>
            <FilterBar
              filters={jobFilters}
              onChange={(name, value) => handleJobFilterChange(name, value as string[])}
              onClearAll={clearJobFilters}
              resultCount={displayRows.length}
            />
            {/* MWA-281 — with nothing on the board at all, name the next step.
                Once other tabs hold applications, stay tab-specific so the
                message never reads as "add one" on Closed or Archived.

                Filtered-to-zero is its own state, because the fix is different:
                one wants an application added, the other wants its filters
                back. The FilterBar above stays mounted either way. */}
            {displayRows.length === 0 ? (
              visibleJobs.length === 0 ? (
                <EmptyState
                  icon={<BusinessCenterIcon />}
                  title="No applications yet"
                  description="Nothing is being tracked for you yet. Roles appear here as they are added to your search."
                />
              ) : hasActiveJobFilters ? (
                <EmptyState
                  icon={<FilterAltOutlinedIcon />}
                  title="No applications match these filters"
                  description={`${rows.length} ${rows.length === 1 ? 'application sits' : 'applications sit'} under ${activeTabLabel}. The filters above are hiding all of them.`}
                  action={
                    <Button variant="outlined" onClick={clearJobFilters}>
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<BusinessCenterIcon />}
                  title={`Nothing in ${activeTabLabel}`}
                  description={emptyTabDescription(activeTab, 'Roles appear here as they reach this stage.')}
                />
              )
            ) : isPhone ? (
              /* Same reason as the staff board: below 768px six columns either
                 scroll sideways or drop, and the card keeps all of them. Warm
                 contacts stay off, matching this table — the portal has never
                 shown a client their own network here. */
              <>
                <Stack spacing={1} sx={{ pb: 1 }}>
                  {pagedRows.map((j) => (
                    <JobApplicationCard
                      key={j.id}
                      job={j}
                      contacts={contacts}
                      showInactivity={!TERMINAL_JOB_TABS.includes(activeTab as TabKey)}
                      decision={j.applyDecision || j.decisionRequested ? renderDecision(j) : null}
                      appliedLabel={formatApplied(j.applicationDate)}
                      onOpen={() => handleJobClick(j)}
                    />
                  ))}
                </Stack>
                <TablePaginationBar
                  count={pageCtl.count}
                  page={pageCtl.page}
                  rowsPerPage={pageCtl.rowsPerPage}
                  setPage={pageCtl.setPage}
                  setRowsPerPage={pageCtl.setRowsPerPage}
                />
              </>
            ) : (
              <Paper variant="outlined" sx={tableStyles.tableContainer}>
                <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
                  <Table stickyHeader sx={{ ...tableStyles.table, ...(isCompact ? getCompactTableSx(theme) : undefined) }}>
                    <TableHead>
                      <TableRow>
                        {/* Job Role has no width: it takes what the others leave. */}
                        <SortableTableHeader id="company" label="Company" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth('20%')} />
                        <SortableTableHeader id="jobTitle" label="Job Role" orderBy={orderBy} order={order} onSort={handleSort} />
                        <SortableTableHeader id="fit" label="Fit" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth({ xs: 44, md: 52 })} />
                        {/* The decision buttons wrap their labels at tablet widths
                            (see wrappingChipSx), so the column can stay narrow
                            without cutting short a choice the client has to make. */}
                        <SortableTableHeader id="decision" label="Your Decision" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth({ xs: '24%', md: 160 })} />
                        {!isCompact && (
                          <SortableTableHeader id="location" label="Location" orderBy={orderBy} order={order} onSort={handleSort} />
                        )}
                        {/* applicationDate = date applied / logged (the app has no separate
                            job-posted date yet). Labeled "Applied" to match the detail dialog. */}
                        <SortableTableHeader id="applied" label="Applied" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth({ xs: 64, md: 84 })} />
                        {isCompact && <ExpanderHeaderCell />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                {pagedRows.map((j) => (
                  <Fragment key={j.id}>
                  <TableRow
                    hover
                    onClick={() => handleJobClick(j)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {j.company}
                        </Typography>
                        {!isCompact && renderCompanyTier(j)}
                        {/* MWA-281 — no staleness warning on the terminal tabs;
                            nothing is expected to move once an application has
                            reached an end state or the client passed on it. */}
                        {!TERMINAL_JOB_TABS.includes(activeTab as TabKey) && (
                          <InactivityChip job={j} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {j.url ? (
                        <Link
                          href={ensureHttpProtocol(j.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {j.jobTitle}
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      ) : (
                        <Typography variant="body2">{j.jobTitle}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {j.fitScore != null ? (
                        <Tooltip title={j.fitScoreReason || 'Job fit score'}>
                          <Chip
                            label={j.fitScore}
                            size="small"
                            sx={{ backgroundColor: fitScoreColor(j.fitScore), color: 'white', fontWeight: 700 }}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={isCompact ? wrappingChipSx : undefined}>{renderDecision(j)}</TableCell>
                    {!isCompact && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {j.location || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatApplied(j.applicationDate)}
                      </Typography>
                    </TableCell>
                    {isCompact && (
                      <ExpanderToggleCell
                        open={expandedRowId === j.id}
                        onToggle={() => setExpandedRowId(expandedRowId === j.id ? null : j.id)}
                        subject={`${j.jobTitle} at ${j.company}`}
                      />
                    )}
                  </TableRow>
                  {isCompact && (
                    <ExpanderRow open={expandedRowId === j.id} colSpan={6}>
                      <ExpanderField label="Location">
                        <Typography variant="body2">{j.location || '—'}</Typography>
                      </ExpanderField>
                      <ExpanderField label="Company Tier">{renderCompanyTier(j)}</ExpanderField>
                    </ExpanderRow>
                  )}
                  </Fragment>
                ))}
                    </TableBody>
                  </Table>
                </Box>
                <TablePaginationBar
                  count={pageCtl.count}
                  page={pageCtl.page}
                  rowsPerPage={pageCtl.rowsPerPage}
                  setPage={pageCtl.setPage}
                  setRowsPerPage={pageCtl.setRowsPerPage}
                />
              </Paper>
            )}
          </>
        )}
      </Box>

      {/* Simple add dialog for portal — reuse the inline pattern */}
      {addOpen && (
        <PortalAddJobDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onAdd={handleCreateJob}
        />
      )}

      <JobDetailDialog
        contacts={contacts}
        interactions={network.interactions}
        contactsLoading={network.loading}
        contactsError={network.error}
        onRetryContacts={network.retry}
        open={detailDialogOpen}
        job={selectedJob}
        onClose={() => setDetailDialogOpen(false)}
        onUpdate={handleJobUpdate}
        onDelete={handleJobDelete}
        onViewInterview={handleViewInterview}
        viewInterviewPending={viewingInterview}
      />

      <InterviewDetailDialog
        open={interviewDialogOpen}
        interview={selectedInterview}
        onClose={handleInterviewClose}
        onUpdate={setSelectedInterview}
        initialSection={interviewSection}
      />
    </Box>
  )
}

